/**
 * Enemy population management.
 *
 * Enemies are placed per-chunk, once, when a chunk first becomes hot, and
 * culled when it goes cold. That coupling is deliberate: chunk residency is
 * already the game's notion of "near the player", already accounts for
 * eviction, and already has a deterministic identity to seed from. Inventing a
 * second spatial index for spawning would be a second thing to keep in sync.
 *
 * Determinism: placement is derived from `hash2(cx, cy, seed)`, not from the
 * shared PRNG. A chunk that is evicted and regenerated must repopulate
 * identically, and consuming shared PRNG state would make that depend on how
 * many other chunks happened to be visited in between.
 */

import { hash2, type Rng } from '../core/rng';
import { profiler } from '../core/profiler';
import { M, VOID } from '../sim/materials';
import { CHUNK_SHIFT, CHUNK_SIZE, Tier, type CellWorld, type TierId } from '../sim/world';
import { solidIn } from './body';
import { Comp, NULL_ENTITY, Team, World } from './ecs';
import {
  EnemyRegistry,
  Rank,
  type BiomeId,
  type EnemyDef,
} from './enemyData';
import { configureEnemy } from './ai/enemyAI';
import type { PixelBodySystem } from './pixelBodySystem';
import { planNameFor } from './pixelBodySystem';

/**
 * Hard ceiling on live enemies. The frame budget, not the fiction: sized
 * against docs/05 §7's "20-enemy scrum, p99 <= 20 ms" and raising it spends
 * that budget. Exported so tests can assert against the number the source
 * actually uses rather than transcribing it.
 */
export const DEFAULT_MAX_LIVE = 48;

/**
 * The coldest tier a chunk may reach while keeping its enemies.
 *
 * Residency, not memory, is what should decide whether a population exists.
 * WARM is ring <= 2 (`world.ts:979-982`) and `ring` counts chunks *beyond* the
 * on-screen span, so this keeps enemies alive for two full chunks — 256 world
 * units at `CHUNK_SIZE` 128 — past every edge of the view. An enemy chasing the
 * player off-screen has that much room before it is allowed to vanish, which is
 * comfortably more than it can cover in the time the player can see the gap.
 */
export const DEFAULT_CULL_TIER: TierId = Tier.WARM;

/**
 * Expected enemies per chunk. May be fractional — see `populateChunk`.
 *
 * Derived from docs/05 §7, not chosen to make a test pass.
 *
 * Once the cull is tied to `DEFAULT_CULL_TIER`, the set of enemies that can
 * reach the player *is* the retained set — so the retained population is the
 * size of the scrum the player can walk into, and docs/05 §7 denominates its
 * heaviest gate in exactly that quantity: "20-enemy scrum + acid + terrain
 * destruction: p99 <= 20 ms". Measured retention under this cull is 34.6-41.5
 * chunks (mean 38.0 over seeds 1 / 42 / 1234), so
 *
 *     20 enemies / 38.0 chunks ~= 0.53 enemies per chunk
 *
 * and 0.5 is that, rounded to something a human can hold.
 *
 * What it measures, with the kill channel switched off so this is spawn density
 * and nothing else: standing population mean 12.6-16.7, peak 20-29. The peak is
 * the scrum, and it lands on the figure it was derived from. Under a player
 * killing 3/s the standing stock roughly halves, to mean 5.9-7.9 and peak 11-18.
 * The gap between 0.53 nominal and ~0.4 delivered is chunks with no standable
 * ground, which `findGround` rejects.
 *
 * That leaves `DEFAULT_MAX_LIVE` at 1.7-4x the observed peak — a valve that a
 * pathological convergence could still reach, and ordinary traversal does not.
 *
 * The old value was 2, an integer, and integers could not express this. The HOT
 * surface is the on-screen chunk span (25 chunks at the shipping 480x270 view),
 * so `perChunk: 2` put 50 enemies on one screen and even `perChunk: 1` put 25 —
 * both at or above the number docs/05 §7 treats as the *pathological* case. The
 * unit had no resolution left below "every chunk is populated", which is the
 * deeper reason the population could only ever be decided by the clamp.
 */
export const DEFAULT_PER_CHUNK = 0.5;

/**
 * Cells of open air directly above a surface cell before it is worth
 * remembering as a candidate at all.
 *
 * This is deliberately small, and it is deliberately *not* "the tallest enemy".
 * It used to be 26 — "tallest routine enemy plus headroom", checked in a single
 * column every third row — and that constant was doing a job it could not do,
 * because at the point it ran nobody had yet decided what was being placed
 * (issue #56). A pre-filter that guesses at a body's size is either too tight
 * (elites are 35-41 cells tall, so 26 admitted sites that could never hold one)
 * or too loose. So it no longer guesses: it rejects only what nothing could
 * stand in — the shortest authored body is 8 cells — and the body's real
 * `halfW`/`halfH` box is tested afterwards by `bodyFitsAt`, once there is a body
 * to test.
 *
 * Every row is checked, not every third. A stride here skips two rows in three
 * and was measured to account for 33.8% of overlaps (docs/17 §1.6 defect c);
 * over six rows the saving is not worth a wrong answer.
 */
const SURFACE_CLEAR = 6;

/** Sample surfaces on a lattice — we need a choice of sites, not every site. */
const SURFACE_STRIDE = 5;

/**
 * How many candidate sites one spawn slot may test before the slot is
 * abandoned.
 *
 * There has to be a bound, because `bodyFitsAt` is a box scan and an
 * unsatisfiable request (a 25x41 elite in a chunk of tunnels) would otherwise
 * pay the full candidate list for nothing. 48 covers roughly two lattice rows
 * either side of the site the hash chose, which is "look along this gallery for
 * somewhere it fits" — and abandoning after that is the right answer, not a
 * compromise. `docs/20` records that every generator studied places entities in
 * a later pass against finished geometry and simply declines when the geometry
 * does not offer a home; committing the spawn anyway is what put 58.9% of them
 * in rock.
 */
const PLACEMENT_TRIES = 48;

/**
 * Does this body's box stand clear at this site?
 *
 * `x, y` is the *feet* convention `spawn` takes: the body centre ends up at
 * `y - def.halfH`, so the box spans `y - 2*halfH` to `y`.
 *
 * Exported because it is the placement contract, and every caller that puts an
 * enemy into the world owes it — `populateChunk` below, and the authored
 * placements in `game.ts` (ambush, biome boss), which today probe a single
 * column exactly as the ambient path used to.
 *
 * `solidIn` rather than a hand-rolled scan: it is the same predicate `stepBody`
 * uses to decide whether a move is legal (`body.ts:299`), so a body that passes
 * here provably has somewhere to go. That matters more than it sounds, because
 * `stepBody` only ever tests a move's *destination* — a body that starts inside
 * rock finds every neighbouring box solid too, and there is no unstick routine
 * anywhere in `src/entity/`. A bad placement is permanent, so the only place to
 * catch it is before it is committed.
 *
 * `phased: false` even for the one authored `BodyFlag.PHASED` def. Phasing is
 * about what a body may move *through*; the complaint this answers is that
 * enemies *appear* embedded in walls, and a ghost that materialises inside rock
 * reads exactly as wrong as a golem does.
 */
export function bodyFitsAt(
  cells: CellWorld,
  def: { halfW: number; halfH: number },
  x: number,
  y: number,
): boolean {
  const halfW = def.halfW;
  const halfH = def.halfH;
  const cy = y - halfH;

  // Only decide against geometry that exists. `isSolid` reads an ungenerated
  // chunk as air (`world.ts:762-764`, and deliberately so: generating a chunk
  // inside a collision probe would be catastrophic), so a box reaching into one
  // would come back "clear" for the worst possible reason. Every authored body
  // is smaller than a chunk, so the box spans at most 2x2 of them and the
  // corners cover it. `updateActive` generates the view plus one ring
  // (`world.ts:957-960`) and only HOT chunks are populated, so in practice the
  // neighbours are always there; this is the check that keeps that a fact
  // rather than an assumption.
  const cx0 = Math.floor(x - halfW) >> CHUNK_SHIFT;
  const cx1 = Math.floor(x + halfW) >> CHUNK_SHIFT;
  const cy0 = Math.floor(cy - halfH) >> CHUNK_SHIFT;
  const cy1 = Math.floor(cy + halfH) >> CHUNK_SHIFT;
  for (let ccy = cy0; ccy <= cy1; ccy++) {
    for (let ccx = cx0; ccx <= cx1; ccx++) {
      if (cells.peekChunk(ccx, ccy) === null) return false;
    }
  }

  return !solidIn(cells, x, cy, halfW, halfH, false);
}

export interface SpawnerOptions {
  /** Hard ceiling on live enemies. The frame budget, not the fiction. */
  maxLive?: number;
  /**
   * Expected enemies per chunk. Fractional values are honoured as an occupancy
   * rate: `0.5` populates half the chunks with one enemy each, deterministically
   * by chunk identity. Actual count also depends on valid ground.
   */
  perChunk?: number;
  /** Cells of clearance the player gets — nothing spawns on top of you. */
  safeRadius?: number;
  /**
   * Fraction of spawns drawn from a *neighbouring* biome's roster.
   * docs/05 §1 puts cross-contamination at 15–25%; it is what makes a cyberpunk
   * drone in the crypt a notable event rather than background noise.
   */
  contamination?: number;
  /**
   * Coldest tier a chunk may hold while keeping its population. Anything
   * colder than this — or evicted outright — has its enemies culled.
   */
  cullTier?: TierId;
}

interface ChunkPop {
  /** Entity handles spawned for this chunk, so culling is O(popsize). */
  entities: number[];
}

export class EnemySpawner {
  readonly registry: EnemyRegistry;
  /** Optional: when present, every spawned enemy gets a pixel body. */
  bodies: PixelBodySystem | null = null;
  private populated = new Map<number, ChunkPop>();
  private maxLive: number;
  private perChunk: number;
  private safeRadius: number;
  private contamination: number;
  private cullTier: TierId;
  private seed: number;

  /** Reusable scratch so placement never allocates. */
  private candX = new Int32Array(256);
  private candY = new Int32Array(256);

  liveCount = 0;

  constructor(
    registry: EnemyRegistry,
    seed: number,
    opts: SpawnerOptions = {},
  ) {
    this.registry = registry;
    this.seed = seed >>> 0;
    this.maxLive = opts.maxLive ?? DEFAULT_MAX_LIVE;
    this.perChunk = opts.perChunk ?? DEFAULT_PER_CHUNK;
    this.safeRadius = opts.safeRadius ?? 90;
    this.contamination = opts.contamination ?? 0.2;
    this.cullTier = opts.cullTier ?? DEFAULT_CULL_TIER;
  }

  /**
   * Populate newly-hot chunks and cull departed ones. Cheap enough to run every
   * sim tick; the work is bounded by how many chunks changed residency.
   */
  update(
    w: World,
    cells: CellWorld,
    playerX: number,
    playerY: number,
    biomeAt: (x: number, y: number) => BiomeId,
    rng: Rng,
  ): void {
    profiler.begin('entities');

    // Recount from the entity table rather than trusting an incrementing
    // counter. Enemies can leave the world through several doors — AI death,
    // chunk cull, capacity eviction — and a tracked count drifts the first time
    // one of them forgets to notify. The scan is over live entities only and
    // runs once per tick, which is cheaper than the class of bug it removes.
    this.liveCount = 0;
    w.each(Comp.ENEMY, () => {
      this.liveCount++;
    });

    // --- populate
    const active = cells.activeChunks;
    for (let i = 0; i < active.length; i++) {
      const c = active[i];
      // Only the hot ring gets a population. Spawning into warm/cold chunks
      // burns the entire budget on enemies four screens away that the player
      // will never meet, and starves the ones they will.
      if (c.tier !== Tier.HOT) continue;
      const key = chunkKey(c.cx, c.cy);
      if (this.populated.has(key)) continue;
      if (this.liveCount >= this.maxLive) break;
      this.populateChunk(w, cells, c.cx, c.cy, playerX, playerY, biomeAt, rng);
    }

    // --- cull chunks that have fallen out of the retention tier
    //
    // This used to fire only when `peekChunk` returned null, i.e. when a chunk
    // was *evicted from memory* (`world.ts:1002`, at >256 resident chunks and a
    // 12-chunk radius). Eviction is a memory policy, not a residency one: it let
    // enemies persist across roughly 2.5x the area they could spawn in (~55-60
    // populated chunks against ~22 HOT ones), so the live count climbed until
    // `maxLive` clipped it and the ceiling — not the design — set the
    // population. Tying the cull to tier makes the retained area a fixed
    // multiple of the populate surface instead of an artefact of the chunk cache
    // size. See `DEFAULT_CULL_TIER` for why WARM and not HOT.
    for (const [key, pop] of this.populated) {
      const cx = (key >> 16) - 32768;
      const cy = (key & 0xffff) - 32768;
      const c = cells.peekChunk(cx, cy);
      if (c !== null && c.tier <= this.cullTier) continue;
      for (const e of pop.entities) {
        if (w.valid(e)) {
          if (this.bodies) this.bodies.detach(e & 0xfffff);
          w.destroy(e);
          this.liveCount--;
        }
      }
      this.populated.delete(key);
    }

    profiler.setCount('enemies', this.liveCount);
    profiler.end('entities');
  }

  private populateChunk(
    w: World,
    cells: CellWorld,
    cx: number,
    cy: number,
    playerX: number,
    playerY: number,
    biomeAt: (x: number, y: number) => BiomeId,
    rng: Rng,
  ): void {
    const pop: ChunkPop = { entities: [] };
    this.populated.set(chunkKey(cx, cy), pop);

    // A fractional `perChunk` resolves to occupancy: at 0.5, half of all chunks
    // get a spawn slot and half get none. Which half is decided by the chunk's
    // own identity, not by draw order, so a chunk that is evicted and returns is
    // still the same kind of place.
    //
    // Ecology is patchy, and "every chunk carries exactly `perChunk` enemies"
    // is what made the smallest expressible density one enemy per 128x128 cells
    // — see `DEFAULT_PER_CHUNK` for why that floor was above the population the
    // design asks for, and so why the clamp was the only thing left deciding it.
    const whole = Math.floor(this.perChunk);
    const frac = this.perChunk - whole;
    let slots = whole;
    if (frac > 0 && hash2(cx, cy, this.seed ^ 0x9e3779b9) % 4096 < frac * 4096) slots++;
    if (slots <= 0) return; // an empty stretch, and cheaper than looking for ground

    const baseX = cx << CHUNK_SHIFT;
    const baseY = cy << CHUNK_SHIFT;
    const biome = biomeAt(baseX + (CHUNK_SIZE >> 1), baseY + (CHUNK_SIZE >> 1));

    const n = this.findGround(cells, baseX, baseY);
    if (n === 0) return;

    for (let k = 0; k < slots && this.liveCount < this.maxLive; k++) {
      // Stateless per-slot hash: same chunk, same slot, same result forever.
      const h = hash2(cx * 31 + k, cy * 17, this.seed);
      const pick = h % n;

      // Never spawn on the player's head. This is a fairness rule, not a
      // performance one — an enemy that materialises inside your safe distance
      // is read as the game cheating. Tested on the site the hash chose, so a
      // slot that the hash aimed at the player is *dropped* rather than
      // relocated — which is what it did before, and what keeps this a
      // clearance fix and not a density change.
      const px0 = this.candX[pick] - playerX;
      const py0 = this.candY[pick] - playerY;
      if (px0 * px0 + py0 * py0 < this.safeRadius * this.safeRadius) continue;

      // Choose the body BEFORE choosing where it goes.
      //
      // This line used to sit *after* `findGround` had already accepted a site,
      // and that ordering was the whole of issue #56: a clearance test that runs
      // before the roster pick cannot consult `halfW` or `halfH`, so the only
      // thing it can check is a column — and a column is not a body. 58.9% of
      // spawns had cells in rock; the overlap rate rose with body width to 100%
      // at 33 cells across, because the wider the body the more of it lay
      // outside the one column that was tested.
      const def = this.pickEnemy(biome, h, biomeAt, this.candX[pick], this.candY[pick]);
      if (!def) continue;

      // ...and only now ask where *this* body fits. The hashed site is still
      // the preference, so placement stays a function of chunk identity; the
      // walk is the relocation, and running out of it is an abandoned spawn.
      let site = -1;
      for (let t = 0; t < PLACEMENT_TRIES && t < n; t++) {
        const c = (pick + t) % n;
        const sx = this.candX[c];
        const sy = this.candY[c];
        const dx = sx - playerX;
        const dy = sy - playerY;
        if (dx * dx + dy * dy < this.safeRadius * this.safeRadius) continue;
        if (!bodyFitsAt(cells, def, sx, sy)) continue;
        site = c;
        break;
      }
      if (site < 0) continue;
      const x = this.candX[site];
      const y = this.candY[site];

      // Swarms arrive as packs; everything else alone. A pack is placed whole —
      // truncating one to fit a budget would turn a swarm into a pair and delete
      // the family docs/05 §3 calls "many weak bodies" — so instead it pays for
      // itself in *frequency*.
      //
      // `perChunk` is a density, so a spawn event that places `packSize` bodies
      // has to happen `packSize` times less often than one that places a single,
      // or an authored number about encounter *shape* silently multiplies the
      // density it was budgeted against. 50 of the 54 authored defs carry
      // `packSize` 1; the four swarms carry 6-8. Without this roll a slot that
      // drew a swarm yielded 8 where the budget said 1, and in a biome whose
      // roster is swarm-heavy that became the ambient density rather than an
      // event — measured on seed 1 the population ran at a median of 25 and a
      // p90 of 60 in exactly such a band, arriving in +6/+8 steps, against a
      // nominal 0.5/chunk.
      //
      // The roll costs nothing the design wanted: expected bodies per slot is 1
      // either way. What changes is that a pack of eight is now an eighth as
      // likely, which is what "swarms are an encounter, not the texture" looks
      // like written down as a number.
      const packSize = Math.max(1, def.packSize);
      if (packSize > 1 && hash2(x, y ^ 0x51ed, this.seed) % packSize !== 0) continue;
      for (let p = 0; p < packSize && this.liveCount < this.maxLive; p++) {
        // The offset used to be applied with no ground check at all — measured
        // at 4.7% of spawns sitting in a column `findGround` itself would have
        // rejected (docs/17 §1.6). A pack is placed *whole*, though, so a member
        // whose offset lands in rock walks back toward the anchor rather than
        // being dropped: the anchor was validated for this body, so the search
        // always terminates, and a swarm never silently becomes a pair.
        let ox = p === 0 ? 0 : (hash2(x, y + p, this.seed) % 17) - 8;
        while (ox !== 0 && !bodyFitsAt(cells, def, x + ox, y)) ox += ox > 0 ? -1 : 1;
        const e = this.spawn(w, cells, def, x + ox, y);
        if (e !== NULL_ENTITY) pop.entities.push(e);
      }
    }
    void rng;
  }

  /**
   * Collect *candidate* standable positions: a solid cell with a little clear
   * space above it. Sampled on a stride rather than exhaustively — we need a
   * choice of spots, not every spot, and 16384 cells per chunk is not worth
   * scanning for it.
   *
   * These are candidates and nothing more. Whether a given body actually fits
   * at one is `bodyFitsAt`'s question, asked in `populateChunk` once the roster
   * pick has happened. This routine deliberately knows nothing about enemy
   * dimensions, because it runs before there is an enemy — the difference from
   * before is that it no longer *pretends* to.
   */
  private findGround(cells: CellWorld, baseX: number, baseY: number): number {
    let n = 0;
    for (
      let ly = CHUNK_SIZE - 2;
      ly >= SURFACE_CLEAR && n < this.candX.length;
      ly -= SURFACE_STRIDE
    ) {
      for (let lx = 2; lx < CHUNK_SIZE - 2 && n < this.candX.length; lx += SURFACE_STRIDE) {
        const x = baseX + lx;
        const y = baseY + ly;
        if (!cells.isSolid(x, y)) continue;
        // Cheap reject for surfaces nothing could stand on — a seam in rubble,
        // the floor of a 3-cell crevice. Every row, no stride.
        let clear = true;
        for (let up = 1; up <= SURFACE_CLEAR; up++) {
          if (cells.isSolid(x, y - up)) {
            clear = false;
            break;
          }
        }
        if (!clear) continue;
        // Don't stand things in lava or acid; they'd die to the sim instantly
        // and read as a bug rather than as ecology.
        const above = cells.peek(x, y - 1);
        if (above !== VOID && M.category[above] === 3 /* LIQUID */) continue;

        this.candX[n] = x;
        this.candY[n] = y - 2;
        n++;
      }
    }
    return n;
  }

  /**
   * Roster pick, with cross-biome contamination. Elites are rare and bosses
   * never spawn from the ambient table — they belong to encounter placement,
   * which is worldgen's business, not the wandering population's.
   */
  private pickEnemy(
    biome: BiomeId,
    h: number,
    biomeAt: (x: number, y: number) => BiomeId,
    x: number,
    y: number,
  ): EnemyDef | undefined {
    const contaminated = (h >>> 8) % 100 < this.contamination * 100;
    let sourceBiome = biome;
    if (contaminated) {
      // Sample a distant point for a plausible neighbour rather than inventing
      // an adjacency table — the generator already decides what borders what.
      const nb = biomeAt(x + 900, y + 400);
      if (nb) sourceBiome = nb;
    }

    const elite = (h >>> 16) % 100 < 8;
    let pool = this.registry.forBiome(sourceBiome, elite ? Rank.ELITE : Rank.BASE);
    // The surface's pool is further cut by the day — docs/27 §12 item 5. The
    // 'surface' tag says an enemy CAN live up there; the dressing says which
    // of them are out in this profile's weather. An empty cut falls through
    // to the tag pool rather than to silence, so a dressing naming a retired
    // id degrades to "any surface enemy" instead of an empty sky.
    if (sourceBiome === 'surface' && this.surfaceRoster !== null) {
      const cut = pool.filter((d) => this.surfaceRoster!.has(d.id));
      if (cut.length > 0) pool = cut;
    }
    if (pool.length === 0) pool = this.registry.forBiome(biome, Rank.BASE);
    if (pool.length === 0) pool = this.registry.defs.filter((d) => d.rank === Rank.BASE);
    if (pool.length === 0) return undefined;
    return pool[(h >>> 4) % pool.length];
  }

  /** The dressing's cut of the surface pool. Null = every tagged def. */
  private surfaceRoster: ReadonlySet<string> | null = null;

  setSurfaceRoster(ids: readonly string[] | null): void {
    this.surfaceRoster = ids === null ? null : new Set(ids);
  }

  /** Create and configure one enemy entity. Returns NULL_ENTITY on failure. */
  spawn(w: World, cells: CellWorld, def: EnemyDef, x: number, y: number): number {
    if (this.liveCount >= this.maxLive) return NULL_ENTITY;
    let e: number;
    try {
      e = w.create(Comp.TRANSFORM | Comp.SPRITE);
    } catch {
      return NULL_ENTITY; // capacity exhausted; not fatal
    }
    const i = e & 0xfffff;
    w.x[i] = x;
    w.y[i] = y - def.halfH;
    w.vx[i] = 0;
    w.vy[i] = 0;
    w.facing[i] = hash2(x, y, this.seed) & 1 ? 1 : -1;
    configureEnemy(w, i, def, this.registry);
    w.team[i] = Team.ENEMY;
    // Body plan comes from proportions and family; materials come from the
    // composition configureEnemy just wrote. The enemy looks like what it is.
    if (this.bodies) {
      this.bodies.attach(w, i, planNameFor(def.family, def.halfW, def.halfH));
    }
    w.aiHome[i * 2] = x;
    w.aiHome[i * 2 + 1] = y;
    this.liveCount++;
    void cells;
    return e;
  }

  /**
   * Retained for the AI context contract. The count is derived in `update()`,
   * so this no longer needs to bookkeep — which is the point: there is now no
   * way for a caller to desynchronise it by forgetting to call this.
   */
  notifyDeath(): void {}

  clear(w: World): void {
    for (const pop of this.populated.values()) {
      for (const e of pop.entities) if (w.valid(e)) w.destroy(e);
    }
    this.populated.clear();
    this.liveCount = 0;
  }
}

function chunkKey(cx: number, cy: number): number {
  return ((cx + 32768) << 16) | (cy + 32768);
}
